// ════════════════════════════════════════════════════════════════════════════
// Grafana "CI Factory Floor" embed-URL builder.
//
// The VA "Tasks" drawer becomes a Grafana render surface (kiosk iframe) per
// server-config/plans/ci-observability-grafana-dashboard.md §5.2. This module owns
// the ONE load-bearing detail that breaks silently if wrong: how the repo filter
// chips map onto the dashboard's `repo` template variable.
//
// Two independent naming systems meet here (verified against the live schema):
//   - /api/work-items/repos returns OWNER/REPO slugs ("inctasoft/voice-assistant")
//     — sourced from services.yaml `github:` fields.
//   - ci_runs.repo (the dashboard's `repo` variable domain) stores the BARE repo
//     name ("voice-assistant") — the collector fills it from GitHub's `.name`.
// So every chip value must have its `owner/` prefix stripped before it becomes a
// `var-repo=` value, or the filter silently matches nothing.
//
// The `repo` variable is multi:true + includeAll:true (dashboard JSON). Grafana's
// URL syntax for a multi-value variable is REPEATED params (var-repo=a&var-repo=b),
// NOT a comma-joined single param. "All selected" (or none) maps to var-repo=$__all.
//
// kiosk + theme=dark: the drawer shows the panels, not Grafana's own chrome. The
// chips drive var-repo from OUTSIDE the frame, sidestepping the Grafana 11.5 kiosk
// leak where in-dashboard variable dropdowns can't be reliably hidden
// (grafana#97759/#96595).
// ════════════════════════════════════════════════════════════════════════════

/** Same-site origin under akrsmv.net — SameSite=Lax cookies ride into the iframe. */
export const GRAFANA_ORIGIN = 'https://grafana.akrsmv.net';

/** The provisioned CI Factory Floor dashboard (uid = ci-factory-floor). */
export const CI_DASHBOARD_PATH = '/d/ci-factory-floor/ci-factory-floor';

/** A direct sign-in link for the no-session fallback (§5.3). */
export const GRAFANA_SIGNIN_URL = `${GRAFANA_ORIGIN}${CI_DASHBOARD_PATH}`;

/**
 * Strip the `owner/` prefix from an "owner/repo" slug, yielding the bare repo name
 * that the dashboard's `repo` variable (ci_runs.repo) actually stores. A value that
 * is already bare passes through unchanged.
 */
export function bareRepoName(ownerRepo: string): string {
  return ownerRepo.includes('/') ? ownerRepo.split('/').pop()! : ownerRepo;
}

export interface GrafanaCiUrlOpts {
  /** The repos the user has chip-selected (owner/repo slugs). */
  selectedRepos: string[];
  /** Every repo the chips can offer (owner/repo slugs). */
  availableRepos: string[];
}

/**
 * Build the kiosk iframe URL for the CI Factory Floor dashboard, translating the
 * chip selection into `var-repo` params.
 *
 *   - empty selection, or every available repo selected → var-repo=$__all
 *   - a proper subset → one repeated var-repo=<bareName> per selected repo (sorted
 *     for a stable, testable URL)
 */
export function buildGrafanaCiUrl({ selectedRepos, availableRepos }: GrafanaCiUrlOpts): string {
  const params = new URLSearchParams();
  // kiosk mode: hide Grafana chrome; theme follows the app's dark surface.
  params.set('kiosk', '');
  params.set('theme', 'dark');

  const selected = selectedRepos.filter(Boolean);
  const isAll =
    selected.length === 0 ||
    (availableRepos.length > 0 && selected.length >= availableRepos.length);

  // URLSearchParams renders `kiosk=` (with a trailing `=`); Grafana treats a bare
  // `kiosk` and `kiosk=` identically, and this keeps the builder pure/testable.
  const base = `${GRAFANA_ORIGIN}${CI_DASHBOARD_PATH}?${params.toString()}`;

  if (isAll) {
    return `${base}&var-repo=$__all`;
  }
  // Repeated params — Grafana's multi-value variable syntax. Owner prefix stripped.
  const repoParams = [...selected]
    .map(bareRepoName)
    .sort()
    .map((r) => `var-repo=${encodeURIComponent(r)}`)
    .join('&');
  return `${base}&${repoParams}`;
}
